import ApplicationController from "../../../application_controller"
import { enter, leave } from "el-transition";
import { computePosition, flip, shift, offset, autoUpdate } from "@floating-ui/dom";

export default class extends ApplicationController {
    static targets = ["dropdownMenu", "selectButton", "arrowUp", "arrowDown", "disabled"]

    connect() {
        // Set up click outside handler
        this.outsideClickHandler = this.outsideClick.bind(this);
        document.addEventListener('click', this.outsideClickHandler);
        this.keydownHandler = this.handleKeydown.bind(this);
        document.addEventListener('keydown', this.keydownHandler);

        // Position tracker
        this.cleanup = null;
    }

    disconnect() {
        this.cleanupPositioning();
        document.removeEventListener('click', this.outsideClickHandler);
        document.removeEventListener('keydown', this.keydownHandler);
    }

    toggleDropdown(event) {
        event.stopPropagation();

        const isHidden = this.dropdownMenuTarget.classList.contains('hidden');

        if (isHidden) {
            this.openDropdown();
        } else {
            this.closeDropdown();
        }
    }

    disabledValue() {
        const input = this.disabledTarget
        return input ? input.value === 'true' : false
    }

    openDropdown() {
        if (this.disabledValue()) { return }
        this.selectButtonTarget.setAttribute('aria-expanded', 'true');
        // Apply selected styles to button
        const styles = this.selectButtonTarget.getAttribute('data-selected-style').split(' ');
        styles.forEach(style => this.selectButtonTarget.classList.add(style));

        // Toggle arrows if present
        this.toggleArrows();

        // Ensure proper z-index
        this.setZIndex(this.dropdownMenuTarget);

        // Set initial position - just to get it in the right place
        const placement = this.getAlignmentPlacement();

        // Position menu but keep it hidden
        this.dropdownMenuTarget.style.visibility = "hidden";
        this.dropdownMenuTarget.classList.remove("hidden");

        // Make sure any origin transforms are cleared initially
        this.dropdownMenuTarget.style.transform = "none";

        // Calculate position immediately before showing
        computePosition(this.selectButtonTarget, this.dropdownMenuTarget, {
            placement,
            middleware: [offset(8), flip(), shift({ padding: 8 })]
        }).then(({ x, y }) => {
            // Apply position
            Object.assign(this.dropdownMenuTarget.style, {
                left: `${x}px`,
                top: `${y}px`,
                position: 'absolute',
                visibility: "visible" // Now make it visible
            });

            // Start animation after positioning is complete
            enter(this.dropdownMenuTarget);

            // Set up tracking for future updates
            this.cleanup = autoUpdate(
                this.selectButtonTarget,
                this.dropdownMenuTarget,
                () => this.updatePosition(placement)
            );
        });
    }

    closeDropdown() {
        this.selectButtonTarget.setAttribute('aria-expanded', 'false');
        // Remove selected styles from button
        const styles = this.selectButtonTarget.getAttribute('data-selected-style').split(' ');
        styles.forEach(style => this.selectButtonTarget.classList.remove(style));

        // Toggle arrows if present
        this.toggleArrows();

        // Stop tracking position changes
        this.cleanupPositioning();

        // Animate out and hide
        leave(this.dropdownMenuTarget).then(() => {
            this.dropdownMenuTarget.classList.add('hidden');
        });

        // Notify listeners
        this.dispatch('closed');
    }

    updatePosition(placement) {
        computePosition(this.selectButtonTarget, this.dropdownMenuTarget, {
            placement,
            middleware: [
                offset(8),           // Space between button and dropdown
                flip(),              // Flip if not enough space
                shift({ padding: 8 }) // Prevent overflow
            ]
        }).then(({ x, y }) => {
            Object.assign(this.dropdownMenuTarget.style, {
                left: `${x}px`,
                top: `${y}px`,
                position: 'absolute'
            });
        });
    }

    getAlignmentPlacement() {
        const classList = this.dropdownMenuTarget.classList;

        if (classList.contains('right-0')) return 'bottom-end';   // Right-aligned
        if (classList.contains('left-0')) return 'bottom-start';  // Left-aligned

        return 'bottom'; // Center-aligned
    }

    cleanupPositioning() {
        if (this.cleanup) {
            this.cleanup();
            this.cleanup = null;
        }
    }

    outsideClick(event) {
        // Close dropdown when clicking outside
        if (!this.element.contains(event.target) &&
            !this.dropdownMenuTarget.classList.contains('hidden')) {
            this.closeDropdown();
        }
    }

    handleKeydown(event) {
        if (event.key !== 'Escape' || this.dropdownMenuTarget.classList.contains('hidden')) return;

        event.preventDefault();
        this.closeDropdown();
        this.selectButtonTarget.focus();
    }

    toggleArrows() {
        if (this.hasArrowUpTarget && this.hasArrowDownTarget) {
            this.arrowUpTarget.classList.toggle('hidden');
            this.arrowDownTarget.classList.toggle('hidden');
        }
    }
}
